import React, { useMemo } from "react"
import { Box, Container, Heading, GridItem, Grid } from "@chakra-ui/react"
import TileCarousel from "../TileCarousel"
import { useMedia } from "@hooks/useMedia"
import ProductCardDrawer from "../ProductCardDrawer"
import { useShopifyProductLive, useShopifyProductRaw } from "@hooks/useShopify"
import ProductCard from "@components/Product/ProductCard"

type Props = {
  id: string
  title: string
  products: GatsbyTypes.SanityProduct[]
  respectStl?: boolean
}

const ProductRecommended: React.FC<Props> = ({ title, products, respectStl = false, currentProductData }) => {
  const [quickViewProduct, setQuickViewProduct] = React.useState<GatsbyTypes.SanityProduct | null>(null)
  const { isBase } = useMedia()
  const spaceBetweenTiles = isBase ? 8 : 16

  const validProducts = React.useMemo(() => {
    return products?.filter(item => item?.shopify?.published !== false)
  }, [products])

  // const hasProductBadge = useMemo(() => validProducts?.some(item => item?.tags?.find(tag => tag?.includes("label"))), [validProducts])

  const isProductsStl = useMemo(() => {
    if (respectStl) {
      return products?.some(item => item?.tags?.find(tag => tag?.includes("stl")))
    }
    return
  }, [respectStl, products])

  const isParentProductStl = useMemo(() => {
    if (currentProductData) {
      return currentProductData?.tags?.find(tag => tag === "stl")
    }
    return
  }, [respectStl, products])

  const carouselSettings = {
    enableScrollbar: true,
    enableNavigation: true,
  } as GatsbyTypes.SanityCarouselSettings

  return validProducts.length ? (
    <>
      <Box bg={isParentProductStl ? "#F3F2EE" : "White"} paddingTop={5} paddingBottom={5} pr={0}>
        <Container
          as="section"
          overflow={`hidden`}
          sx={{
            ".swiper": {
              overflow: "visible",
            },
          }}
        >
          <Heading as="h2" size="h2" mb={2.5} textAlign="center">
            {isParentProductStl ? "You May Also Like" : title}
          </Heading>
          {!isBase & isProductsStl ? (
            <Grid templateColumns="repeat(3, 1fr)" gap={6}>
              {validProducts?.map((item: any) => (
                <GridItem key={item?.shopify?.id} width="100%">
                  <ProductCard
                    item={item}
                    location={location}
                    fullSize
                    onQuickView={setQuickViewProduct}
                    collection={item?.promaryCollection}
                    wishlistSize={"lg"}
                    showPlus={false}
                    isStl={true}
                  />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <TileCarousel
              carouselSettings={carouselSettings}
              spaceBetweenTiles={spaceBetweenTiles}
              slidesPerView={isProductsStl || isParentProductStl ? 4 : 6}
              slidesPerViewMobile={isProductsStl || isParentProductStl ? 1.15 : 1.25}
              navigationProps={{ transform: `50%`, top: "calc(490px / 2)", bg: "brand.primary", color: "black" }}
            >
              {validProducts?.map((item: any) => <RelatedItem product={item} key={item?.id} onQuickView={setQuickViewProduct} />)}
            </TileCarousel>
          )}
        </Container>
      </Box>
      {quickViewProduct && (
        <ProductCardDrawer
          product={quickViewProduct}
          updateQuickViewProduct={product => setQuickViewProduct(product)}
          onClose={() => setQuickViewProduct(null)}
        />
      )}
    </>
  ) : null
}

export default React.memo(ProductRecommended)

export const RelatedItem = ({
  product,
  location,
  onQuickView,
}: {
  product: GatsbyTypes.SanityProduct
  location: Location
  onQuickView?: () => void
}) => {
  const { product: rawProduct } = useShopifyProductRaw(product)
  const { product: liveProduct } = useShopifyProductLive(rawProduct)
  const item = liveProduct || rawProduct

  const isStl = product?.tags?.includes(`stl`)

  return (
    <Box mb={3.5} minWidth="full">
      <ProductCard
        isStl={isStl}
        fullSize={true}
        key={item?.id}
        item={item}
        location={location}
        wishlistSize="sm"
        onQuickView={onQuickView}
      />
    </Box>
  )
}
